import {
  css, type Interpolation,
} from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  type FC, type ReactNode,
} from 'react';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { type ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import {
  RadioGroupComponent, type RadioGroupItem,
} from '~/_shared/baseComponents/radio/radioGroup.component';
import { HintTooltipComponent } from '~/_shared/baseComponents/tooltip/hintTooltip.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useIsMapTopViewBlocked } from '~/map/map/loader/overMapLoader.container';
import { TooltipComponent } from '../baseComponents/tooltip/tooltip.component';
import { FloatingBoxComponent } from '../components/floatingBox/floatingBox.component';
import { FloatingBoxWrapper } from '../components/floatingBox/floatingBoxWrapper.component';
import { useTranslation } from '../utils/hooks';

const CONTENT_LABEL_GAP = 10;

const dropdownTriggerStyle = (theme: Theme) => css({
  color: theme.textColors.primary,
  height: 40,
  marginRight: 10,
  width: 'auto',
});

const floatingBoxWrapperStyle = css({
  '--offset': '20px',
  top: 'var(--offset)',
  left: 'var(--offset)',
  right: 'calc(var(--offset) + 40px)', // 40px is for the map tools
  pointerEvents: 'none',
  height: 'calc(100% - (var(--offset) * 2) - 106px)', // the 106px is for the zoom controls

  '& > *': {
    pointerEvents: 'auto',
  },
});

const floatingBoxContentStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  alignContent: 'flex-start',
  pointerEvents: 'none',

  '& > *': {
    pointerEvents: 'auto',
  },
});

const buttonsWrapperStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
});

const triggerHiddenIconStyle = css({
  display: 'none',
});

const buttonStyle = css({
  padding: '8px 16px',
});

const dropdownItemStyle = ({ roundTopRightCorner }: Readonly<{ roundTopRightCorner: boolean }>) => (theme: Theme) => css({
  color: theme.textColors.primary,
  fontSize: '18px',
  padding: 20,
  height: 'auto',
  '&:first-of-type': {
    borderRadius: roundTopRightCorner ? '0 4px 0 0' : undefined,
  },
});

const dropdownListStyle = ({ roundTopRightCorner }: { roundTopRightCorner: boolean }) => css({
  minWidth: 150,
  borderRadius: roundTopRightCorner ? '0 4px 4px 4px' : '0 0 4px 4px',
});

const dropdownItemIconStyle = css({
  marginRight: 8,
});

const sectionHeadingStyle = css({
  textTransform: 'uppercase',
});

const radioGroupStyle = css({
  marginTop: 10,
});

const radioItemStyle = css({
  fontSize: '14px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  '&:first-of-type': {
    marginLeft: 0,
  },
});

const checkboxTextStyle = css({
  marginLeft: 6,
  textTransform: 'capitalize',
});

export type MapConfirmButtonProps = {
  icon: IconProp;
  onClick?: () => void;
  buttonStyle?: ButtonStyle;
  label: string;
  className?: string;
  isDisabled?: boolean;
  tooltip?: string;
  hint?: string;
};

export type MapConfirmProps = Readonly<{
  isOpen: boolean;
  onClose?: () => void;
  dropdownOptions?: ReadonlyArray<DropdownOption<string>>;
  dropdownListStyle?: Interpolation;
  dropdownHeading?: string;
  selectedOptionId?: string | null;
  onDropdownSelect?: (optionId: string) => void;
  heading: string;
  dropdownTriggerStyle?: Interpolation;
  triggerPlaceholderStyle?: Interpolation;
  className?: string;
  buttons: MapConfirmButtonProps[];
  buttonsWrapperStyle?: Interpolation;
  onShowHiddenElementsChange?: (value: boolean) => void;
  radioHeading?: string;
  radio?: {
    heading: string;
    items: ReadonlyArray<RadioGroupItem<string>>;
    onChange: (value: string) => void;
    selectedValue: string;
  };
  selectionGuide?: ReactNode;
  secondRow?: ReactNode;
  showHiddenElements?: boolean;
}>;

////////////////////////////////////////////////////////////////////////////////////////////////////
// When this component is used on the map, don't forget to register action in useIsMapTopViewBlocked
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
useIsMapTopViewBlocked;
////////////////////////////////////////////////////////////////////////////////////////////////////
export const MapConfirmComponent: FC<MapConfirmProps> = ({
  showHiddenElements, onShowHiddenElementsChange, dropdownOptions,
  ...props
}) => {
  const [t] = useTranslation();

  if (!props.isOpen) {
    return null;
  }

  const roundTopRightCorner = props.selectedOptionId !== null;
  const showDropdown = dropdownOptions && (dropdownOptions.length > 0);

  return (
    <FloatingBoxWrapper css={floatingBoxWrapperStyle}>
      <div css={floatingBoxContentStyle}>
        <FloatingBoxComponent
          className={props.className}
          gap={20}
          heading={props.heading}
          isOpen={props.isOpen}
          onClose={props.onClose}
        >
          {showDropdown && (
            <Stack gap={CONTENT_LABEL_GAP}>
              {props.dropdownHeading !== undefined &&
                <p css={sectionHeadingStyle}>{props.dropdownHeading}</p>
              }
              <RegularDropdownComponent
                inPortal
                itemStyle={dropdownItemStyle({ roundTopRightCorner })}
                itemIconStyle={dropdownItemIconStyle}
                options={dropdownOptions}
                triggerStyle={[dropdownTriggerStyle, props.dropdownTriggerStyle]}
                triggerPlaceholderStyle={props.triggerPlaceholderStyle}
                value={props.selectedOptionId}
                listStyle={[css(dropdownListStyle({ roundTopRightCorner })), props.dropdownListStyle]}
                onChange={(id) => {
                  if (id !== undefined && props.onDropdownSelect) {
                    props.onDropdownSelect(id);
                  }
                }}
                placeholder={t('Select Desired Option')}
                isDisabled={dropdownOptions.length === 1}
                triggerIconStyle={dropdownOptions.length === 1 ? triggerHiddenIconStyle : undefined}
              />
            </Stack>
          )}

          {props.radio && (
            <Stack gap={CONTENT_LABEL_GAP}>
              <p css={sectionHeadingStyle}>{props.radio.heading}</p>

              <RadioGroupComponent
                css={radioGroupStyle}
                itemCommonStyle={radioItemStyle}
                items={props.radio.items}
                onValueChange={props.radio.onChange}
                selectedValue={props.radio.selectedValue}
              />
            </Stack>
          )}

          {onShowHiddenElementsChange && notNullsy(showHiddenElements) && (
            <CheckboxComponent
              checkedSetter={() => onShowHiddenElementsChange(!showHiddenElements)}
              isChecked={showHiddenElements}
              text={t('BoundaryEdit.overlay.showElements')}
              textStyle={checkboxTextStyle}
            />
          )}

          {props.buttons.length > 0 && (
            <div css={[buttonsWrapperStyle, props.buttonsWrapperStyle]}>
              {props.buttons.map((button, index) => (
                <Inline gap={4} key={index}>
                  {button.hint && <HintTooltipComponent tooltipContent={button.hint} size={16} />}
                  <TooltipComponent tooltipContent={button.tooltip}>
                    <ButtonComponent
                      prefixIcon={button.icon}
                      text={button.label}
                      css={buttonStyle}
                      buttonStyle={button.buttonStyle}
                      onClick={button.onClick}
                      isDisabled={button.isDisabled}
                    />
                  </TooltipComponent>
                </Inline>
              ))}
            </div>
          )}
        </FloatingBoxComponent>
        {props.selectionGuide}
      </div>
      {props.secondRow}
    </FloatingBoxWrapper>
  );
};
